import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Check, Layers, ListChecks, X } from 'lucide-react';
import { formBuilderApi } from '../api';
import { FORM_TAG_LABELS, FormTag, Option } from '../types';
import { iconButton, primaryButton, secondaryButton } from '../ui';
import { inputSmall } from './panelParts';

type NewFormDialogProps = {
  onClose: () => void;
  onContinue: (selection: { sensor_type: string; category: string; tags: FormTag[] }) => void;
};

const TAG_CHOICES: { value: FormTag; hint: string; icon: typeof Layers }[] = [
  { value: 'single_calibration', hint: 'One sensor at a time', icon: Layers },
  { value: 'bulk_calibration', hint: 'Many sensors in one run', icon: ListChecks },
];

/** First step of "New form": sensor type, category + calibration tag, chosen before the editor opens. */
export default function NewFormDialog({ onClose, onContinue }: NewFormDialogProps) {
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [sensorType, setSensorType] = useState('');
  const [categoryOptions, setCategoryOptions] = useState<Option[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoryError, setCategoryError] = useState<string>();
  const [category, setCategory] = useState('');
  const [tag, setTag] = useState<FormTag>();

  const loadOptions = () => {
    setLoading(true);
    setError(undefined);
    formBuilderApi.sensorTypeOptions()
      .then(setOptions)
      .catch((e) => setError(e?.response?.data?.detail || e?.message || 'Failed to load sensor types'))
      .finally(() => setLoading(false));
  };
  useEffect(loadOptions, []);

  // Categories depend on the sensor type: reload (and clear the choice) whenever it changes.
  useEffect(() => {
    setCategory('');
    setCategoryOptions([]);
    setCategoryError(undefined);
    if (!sensorType) return;
    let cancelled = false;
    setCategoriesLoading(true);
    formBuilderApi.categoryOptions(sensorType)
      .then((result) => { if (!cancelled) setCategoryOptions(result); })
      .catch((e) => { if (!cancelled) setCategoryError(e?.response?.data?.detail || e?.message || 'Failed to load categories'); })
      .finally(() => { if (!cancelled) setCategoriesLoading(false); });
    return () => { cancelled = true; };
  }, [sensorType]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const ready = useMemo(() => !!sensorType && !!category && !!tag, [sensorType, category, tag]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4 backdrop-blur-[2px]">
      <div role="dialog" aria-modal="true" aria-labelledby="new-form-title" className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="new-form-title" className="text-sm font-semibold text-slate-900">New form</h2>
            <p className="mt-1 text-[11px] leading-4 text-slate-500">Choose what this form is for. Every data source in the form can use the sensor type and category, and they can’t be changed later. Only one single and one bulk calibration form can exist for each sensor type and category.</p>
          </div>
          <button type="button" className={iconButton} onClick={onClose} aria-label="Close new form dialog"><X className="h-3.5 w-3.5" /></button>
        </div>

        <div className="mt-4 space-y-4">
          <label className="block">
            <span className="mb-1 block text-[10px] font-medium text-slate-600">1. Sensor type</span>
            <select autoFocus className={inputSmall()} value={sensorType} disabled={loading || !!error} onChange={(e) => setSensorType(e.target.value)}>
              <option value="">{loading ? 'Loading sensor types…' : 'Select a sensor type'}</option>
              {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {error && (
              <span role="alert" className="mt-1 block text-[11px] text-red-600">
                {error} <button type="button" className="font-medium underline" onClick={loadOptions}>Retry</button>
              </span>
            )}
          </label>

          <label className="block">
            <span className="mb-1 block text-[10px] font-medium text-slate-600">2. Category</span>
            <select className={inputSmall()} value={category} disabled={!sensorType || categoriesLoading || !!categoryError} onChange={(e) => setCategory(e.target.value)}>
              <option value="">{!sensorType ? 'Select a sensor type first' : categoriesLoading ? 'Loading categories…' : 'Select a category'}</option>
              {categoryOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {categoryError && <span role="alert" className="mt-1 block text-[11px] text-red-600">{categoryError}</span>}
          </label>

          <fieldset>
            <legend className="mb-1 block text-[10px] font-medium text-slate-600">3. Calibration type</legend>
            <div className="grid grid-cols-2 gap-2" role="radiogroup">
              {TAG_CHOICES.map(({ value, hint, icon: Icon }) => {
                const selected = tag === value;
                return (
                  <button key={value} type="button" role="radio" aria-checked={selected} onClick={() => setTag(value)}
                    className={`relative flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition ${selected ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-100' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}>
                    <Icon className={`h-4 w-4 ${selected ? 'text-primary-600' : 'text-slate-400'}`} />
                    <span className="text-[12px] font-semibold text-slate-800">{FORM_TAG_LABELS[value]}</span>
                    <span className="text-[10px] text-slate-500">{hint}</span>
                    {selected && <Check className="absolute right-2 top-2 h-3.5 w-3.5 text-primary-600" />}
                  </button>
                );
              })}
            </div>
          </fieldset>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className={secondaryButton} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryButton} disabled={!ready}
            onClick={() => ready && onContinue({ sensor_type: sensorType, category, tags: [tag as FormTag] })}>
            Continue <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
